import type { RentalAgreementDto } from '../../services/api';
import { i18nService, t } from '../../services/i18n';
import { escapeHtml } from './property-shared';

type AgreementPeriod = {
  agreement: RentalAgreementDto;
  start: Date;
  endInclusive: Date;
  endExclusive: Date;
  durationDays: number;
};

type OccupancySegment = {
  kind: 'occupied' | 'vacant';
  start: Date;
  endExclusive: Date;
  durationDays: number;
  agreement?: RentalAgreementDto;
};

type TenantSwitch = {
  fromAgreement: RentalAgreementDto;
  toAgreement: RentalAgreementDto;
  date: Date;
  vacancyDays: number;
};

type OccupancySummary = {
  periods: AgreementPeriod[];
  segments: OccupancySegment[];
  switches: TenantSwitch[];
  occupiedDays: number;
  vacantDays: number;
  averageLeaseDays: number;
  windowStart: Date;
  windowEndExclusive: Date;
};

function parseDate(dateStr: string): Date {
  const [year, month, day] = dateStr.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

function addDays(date: Date, days: number): Date {
  const value = new Date(date);
  value.setDate(value.getDate() + days);
  return value;
}

function startOfToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

function diffDays(start: Date, endExclusive: Date): number {
  return Math.max(0, Math.round((endExclusive.getTime() - start.getTime()) / 86400000));
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function formatDate(date: Date): string {
  return i18nService.formatDate(date.toISOString().slice(0, 10), { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDuration(days: number): string {
  if (days <= 0) {
    return t('property.occupancy.duration.none');
  }

  if (days < 30) {
    return t('property.occupancy.duration.days', { count: days });
  }

  const months = Math.floor(days / 30);
  const restDays = days % 30;
  if (restDays === 0) {
    return t('property.occupancy.duration.months', { count: months });
  }

  return t('property.occupancy.duration.monthsAndDays', { months, days: restDays });
}

function formatSegmentLabel(segment: OccupancySegment): string {
  if (segment.kind === 'vacant') {
    return t('property.occupancy.vacancyLabel');
  }

  return segment.agreement?.tenantName || t('property.occupancy.occupiedLabel');
}

function toPercent(date: Date, start: Date, endExclusive: Date): number {
  const total = endExclusive.getTime() - start.getTime();
  if (total <= 0) {
    return 0;
  }

  return clamp(((date.getTime() - start.getTime()) / total) * 100, 0, 100);
}

function buildSummary(agreements: RentalAgreementDto[]): OccupancySummary | null {
  const today = startOfToday();

  const periods = agreements
    .filter((agreement) => Boolean(agreement.startDate))
    .map((agreement) => ({ agreement, start: parseDate(agreement.startDate!) }))
    .filter(({ start }) => start <= today)
    .sort((left, right) => left.start.getTime() - right.start.getTime())
    .map(({ agreement, start }) => {
      const rawEndInclusive = agreement.endDate ? parseDate(agreement.endDate) : today;
      const endInclusive = rawEndInclusive < start ? start : rawEndInclusive;
      const endExclusive = addDays(endInclusive, 1);
      return {
        agreement,
        start,
        endInclusive,
        endExclusive,
        durationDays: diffDays(start, endExclusive),
      } satisfies AgreementPeriod;
    });

  if (periods.length === 0) {
    return null;
  }

  const segments: OccupancySegment[] = [];
  const switches: TenantSwitch[] = [];
  let occupiedDays = 0;
  let vacantDays = 0;

  periods.forEach((period, index) => {
    segments.push({
      kind: 'occupied',
      start: period.start,
      endExclusive: period.endExclusive,
      durationDays: period.durationDays,
      agreement: period.agreement,
    });
    occupiedDays += period.durationDays;

    const next = periods[index + 1];
    if (!next) {
      return;
    }

    const vacancyStart = period.endExclusive;
    const vacancyEnd = next.start;
    const vacancyDays = diffDays(vacancyStart, vacancyEnd);
    if (vacancyDays > 0) {
      segments.push({
        kind: 'vacant',
        start: vacancyStart,
        endExclusive: vacancyEnd,
        durationDays: vacancyDays,
      });
      vacantDays += vacancyDays;
    }

    if (period.agreement.tenantId !== next.agreement.tenantId) {
      switches.push({
        fromAgreement: period.agreement,
        toAgreement: next.agreement,
        date: next.start,
        vacancyDays,
      });
    }
  });

  const lastPeriod = periods[periods.length - 1];
  const todayExclusive = addDays(today, 1);
  if (lastPeriod.endExclusive < todayExclusive) {
    const currentVacancyDays = diffDays(lastPeriod.endExclusive, todayExclusive);
    if (currentVacancyDays > 0) {
      segments.push({
        kind: 'vacant',
        start: lastPeriod.endExclusive,
        endExclusive: todayExclusive,
        durationDays: currentVacancyDays,
      });
      vacantDays += currentVacancyDays;
    }
  }

  return {
    periods,
    segments,
    switches,
    occupiedDays,
    vacantDays,
    averageLeaseDays: Math.round(occupiedDays / periods.length),
    windowStart: periods[0].start,
    windowEndExclusive: segments[segments.length - 1]?.endExclusive ?? lastPeriod.endExclusive,
  };
}

function renderSummaryCard(labelKey: string, value: string, tone: 'default' | 'success' | 'warning' | 'info' = 'default'): string {
  return `
    <article class="occupancy-summary-card occupancy-summary-card--${tone}">
      <span class="occupancy-summary-label">${t(labelKey)}</span>
      <strong class="occupancy-summary-value">${value}</strong>
    </article>
  `;
}

function renderSegment(segment: OccupancySegment, windowStart: Date, windowEndExclusive: Date): string {
  const left = toPercent(segment.start, windowStart, windowEndExclusive);
  const right = toPercent(segment.endExclusive, windowStart, windowEndExclusive);
  const width = Math.max(right - left, 1.2);
  const label = formatSegmentLabel(segment);
  const tooltip = `${label} - ${formatDate(segment.start)} ${t('property.occupancy.until')} ${formatDate(addDays(segment.endExclusive, -1))}`;

  return `
    <div
      class="occupancy-segment occupancy-segment--${segment.kind}"
      style="left:${left.toFixed(2)}%;width:${width.toFixed(2)}%"
      title="${escapeHtml(`${tooltip} (${formatDuration(segment.durationDays)})`)}"
    >
      <span class="occupancy-segment-label">${escapeHtml(label)}</span>
    </div>
  `;
}

function renderSwitchMarker(item: TenantSwitch, windowStart: Date, windowEndExclusive: Date): string {
  const left = toPercent(item.date, windowStart, windowEndExclusive);
  const tooltip = `${item.fromAgreement.tenantName} -> ${item.toAgreement.tenantName}`;
  return `
    <button
      type="button"
      class="occupancy-switch-marker"
      style="left:${left.toFixed(2)}%"
      title="${escapeHtml(tooltip)}"
      aria-label="${escapeHtml(tooltip)}"
    ></button>
  `;
}

function renderSwitchList(switches: TenantSwitch[]): string {
  if (switches.length === 0) {
    return `<div class="empty-state occupancy-switches-empty"><p>${t('property.occupancy.noSwitches')}</p></div>`;
  }

  return `
    <div class="occupancy-switch-list">
      ${switches.map((item) => `
        <article class="occupancy-switch-item">
          <div>
            <div class="occupancy-switch-date">${formatDate(item.date)}</div>
            <div class="occupancy-switch-copy">${escapeHtml(item.fromAgreement.tenantName)} -> ${escapeHtml(item.toAgreement.tenantName)}</div>
          </div>
          <span class="badge ${item.vacancyDays > 0 ? 'badge-warning' : 'badge-info'}">
            ${item.vacancyDays > 0
              ? t('property.occupancy.switchAfterVacancy', { duration: formatDuration(item.vacancyDays) })
              : t('property.occupancy.switchDirect')}
          </span>
        </article>
      `).join('')}
    </div>
  `;
}

export class RcPropertyOccupancyTab extends HTMLElement {
  agreements: RentalAgreementDto[] = [];

  render(): void {
    const summary = buildSummary(this.agreements);

    if (!summary) {
      this.innerHTML = `
        <section class="occupancy-card">
          <div class="occupancy-header">
            <div>
              <h3>${t('property.occupancy.title')}</h3>
              <p>${t('property.occupancy.subtitle')}</p>
            </div>
          </div>
          <div class="empty-state occupancy-empty"><p>${t('property.occupancy.empty')}</p></div>
        </section>
      `;
      return;
    }

    const currentSegment = summary.segments[summary.segments.length - 1];
    const statusLabel = currentSegment?.kind === 'vacant'
      ? t('property.occupancy.currentlyVacant')
      : t('property.occupancy.currentlyOccupied');

    this.innerHTML = `
      <section class="occupancy-card">
        <div class="occupancy-header">
          <div>
            <h3>${t('property.occupancy.title')}</h3>
            <p>${t('property.occupancy.subtitle')}</p>
          </div>
          <div class="occupancy-header-badges">
            <span class="badge ${currentSegment?.kind === 'vacant' ? 'badge-warning' : 'badge-success'}">${statusLabel}</span>
            <span class="badge badge-info">${summary.periods.length} ${t('property.occupancy.contractCount')}</span>
          </div>
        </div>

        <div class="occupancy-summary-grid">
          ${renderSummaryCard('property.occupancy.summary.rented', formatDuration(summary.occupiedDays), 'success')}
          ${renderSummaryCard('property.occupancy.summary.vacant', formatDuration(summary.vacantDays), 'warning')}
          ${renderSummaryCard('property.occupancy.summary.averageLease', formatDuration(summary.averageLeaseDays), 'info')}
          ${renderSummaryCard('property.occupancy.summary.switches', String(summary.switches.length))}
        </div>

        <div class="occupancy-track-card">
          <div class="occupancy-track-meta">
            <span>${formatDate(summary.windowStart)}</span>
            <span>${formatDate(addDays(summary.windowEndExclusive, -1))}</span>
          </div>
          <div class="occupancy-track-shell">
            <div class="occupancy-track">
              ${summary.segments.map((segment) => renderSegment(segment, summary.windowStart, summary.windowEndExclusive)).join('')}
              ${summary.switches.map((item) => renderSwitchMarker(item, summary.windowStart, summary.windowEndExclusive)).join('')}
            </div>
          </div>
          <div class="occupancy-legend">
            <span class="occupancy-legend-item"><span class="occupancy-legend-dot occupancy-legend-dot--occupied"></span>${t('property.occupancy.legend.occupied')}</span>
            <span class="occupancy-legend-item"><span class="occupancy-legend-dot occupancy-legend-dot--vacant"></span>${t('property.occupancy.legend.vacant')}</span>
            <span class="occupancy-legend-item"><span class="occupancy-legend-dot occupancy-legend-dot--switch"></span>${t('property.occupancy.legend.switch')}</span>
          </div>
        </div>

        <div class="occupancy-switches-card">
          <div class="occupancy-switches-header">
            <div>
              <h4>${t('property.occupancy.switchesTitle')}</h4>
              <p>${t('property.occupancy.switchesSubtitle')}</p>
            </div>
          </div>
          ${renderSwitchList(summary.switches)}
        </div>
      </section>
    `;
  }
}

if (!customElements.get('rc-property-occupancy-tab')) {
  customElements.define('rc-property-occupancy-tab', RcPropertyOccupancyTab);
}